import React, { useState, useEffect } from "react";
import { Rnd } from "react-rnd";
import * as XLSX from "xlsx";

import "./LaboratoryAnalysisFaceplate.css";

function LaboratoryAnalysisFaceplate({
  close = () => {},
  defaultPosition = {
    x: 150,
    y: 80
  }
}) {

  const [sample, setSample] = useState(null);

  const [faceplatePos, setFaceplatePos] = useState(() => {

    const saved =
      localStorage.getItem(
        "LaboratoryAnalysisFaceplatePos"
      );

    return saved
      ? JSON.parse(saved)
      : defaultPosition;
  });

  useEffect(() => {

    localStorage.setItem(
      "LaboratoryAnalysisFaceplatePos",
      JSON.stringify(faceplatePos)
    );

  }, [faceplatePos]);

  /* ==========================================
     LOAD SAMPLE
  ========================================== */

  useEffect(() => {

    const saved =
      localStorage.getItem(
        "CurrentLabSample"
      );

    if (saved) {
      setSample(JSON.parse(saved));
    }

  }, []);

  if (!sample) {
    return null;
  }

  const composition =
    sample.composition || {};

  const source =
    sample.source || "Unknown";

  /* ==========================================
     DISTILLATION RANGES
  ========================================== */

  const ranges = {

    LightNaphtha:
      "IBP - 90°C",

    HeavyNaphtha:
      "90 - 180°C",

    Kerosene:
      "180 - 240°C",

    Diesel:
      "240 - 360°C",

    VGO:
      "360 - 540°C",

    Residue:
      ">540°C"

  };

  /* ==========================================
     TOTAL %
  ========================================== */

  const totalPercent =
    Object.values(composition)
      .reduce(
        (a, b) => a + Number(b || 0),
        0
      );

  /* ==========================================
     PRINT
  ========================================== */

  const handlePrint = () => {

    window.print();

  };

  /* ==========================================
     EXPORT EXCEL
  ========================================== */

  const handleExportExcel = () => {

    const rows =
      Object.entries(composition).map(
        ([name, value]) => ({
          Product: name,
          Composition_wt_pct:
            Number(value).toFixed(3),
          Distillation_Range:
            ranges[name] || "-"
        })
      );

    const ws =
      XLSX.utils.json_to_sheet(rows);

    const wb =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      "Lab Analysis"
    );

    XLSX.writeFile(
      wb,
      `${source}_Lab_Report.xlsx`
    );
  };

  /* ==========================================
     SHARE
  ========================================== */

  const handleShare = async () => {

    try {

      await navigator.clipboard.writeText(
        JSON.stringify(sample, null, 2)
      );

      alert(
        "Laboratory report copied to clipboard."
      );

    } catch {

      alert(
        "Unable to copy report."
      );

    }
  };

  return (

    <Rnd
      position={faceplatePos}
      onDragStop={(e, d) =>
        setFaceplatePos({
          x: d.x,
          y: d.y
        })
      }
      minWidth={850}
      minHeight={650}
      dragHandleClassName="lab-header"
      className="lab-rnd"
    >

      <div className="lab-faceplate">

        {/* ==========================================
           HEADER
        ========================================== */}

        <div className="lab-header">

          <div className="lab-title">
            LABORATORY ANALYSIS REPORT
          </div>

          <button
            className="lab-close-btn"
            onClick={close}
          >
            ✕
          </button>

        </div>

        {/* ==========================================
           SAMPLE INFO
        ========================================== */}

        <div className="lab-sample-info">

          <div>
            <strong>Sample Source:</strong>{" "}
            {source}
          </div>

          <div>
            <strong>Date:</strong>{" "}
            {new Date().toLocaleString()}
          </div>

        </div>

        {/* ==========================================
           COMPOSITION TABLE
        ========================================== */}

        <div className="lab-body">

          <table className="lab-table">

            <thead>

              <tr>

                <th>Product</th>

                <th>
                  Composition
                  (wt%)
                </th>

                <th>
                  Distillation Range
                </th>

              </tr>

            </thead>

            <tbody>

              {Object.entries(composition)
                .map(
                  ([name, value]) => (

                    <tr key={name}>

                      <td>{name}</td>

                      <td>
                        {Number(
                          value
                        ).toFixed(3)}
                      </td>

                      <td>
                        {ranges[name] || "-"}
                      </td>

                    </tr>

                  )
                )}

            </tbody>

          </table>

        </div>

        {/* ==========================================
           SUMMARY
        ========================================== */}

        <div className="lab-summary">

          <div className="lab-summary-card">

            <div>
              Total Composition
            </div>

            <div>

              {totalPercent.toFixed(3)}
              %

            </div>

          </div>

        </div>

        {/* ==========================================
           DISCLAIMER
        ========================================== */}

        <div className="lab-disclaimer">

          <strong>DISCLAIMER:</strong>

          This laboratory analysis report
          is generated by the NK Refinery
          Digital Twin System and is
          intended for training,
          simulation, educational,
          process monitoring and
          demonstration purposes only.
            <br />
          Values shown represent simulated
          stream compositions derived from
          process calculations and should
          not be used for custody transfer,
          commercial transactions or
          certified laboratory reporting.

        </div>

        {/* ==========================================
           FOOTER
        ========================================== */}

        <div className="lab-footer">

          <div className="lab-footer-title">
            NK Refinery Digital Twin System
          </div>

          <div className="lab-footer-actions">

            <button
              className="lab-btn"
              onClick={handlePrint}
            >
              Print
            </button>

            <button
              className="lab-btn"
              onClick={handleShare}
            >
              Share
            </button>

            <button
              className="lab-btn"
              onClick={handleExportExcel}
            >
              Export Excel
            </button>

          </div>

        </div>

      </div>

    </Rnd>

  );
}

export default LaboratoryAnalysisFaceplate;